import { resolveOwnershipApiUrl } from './ownershipApi';

export type OwnershipProvider = 'google' | 'facebook' | 'shopify';

interface OAuthInitResponse {
  success: boolean;
  auth_url?: string;
  error?: string;
}

/**
 * Kick off the OAuth flow for campsite ownership management.
 * The backend returns an auth URL which we redirect the browser to.
 */
export const beginOwnershipOAuth = async (
  provider: OwnershipProvider,
  returnUrl?: string,
  redirectOverride?: string
): Promise<void> => {
  if (typeof window === 'undefined') {
    throw new Error('OAuth sign-in can only run in the browser.');
  }

  // Always return to the owner portal so we can process the token client-side.
  const ownerPortalReturn = window.location.href;
  const desiredPostAuthReturn = returnUrl || ownerPortalReturn;

  const endpoint = resolveOwnershipApiUrl(`/auth/${provider}/login`);
  const loginUrl =
    endpoint.startsWith('http://') || endpoint.startsWith('https://')
      ? new URL(endpoint)
      : new URL(endpoint, window.location.origin);

  loginUrl.searchParams.set('return_url', ownerPortalReturn);

  if (redirectOverride) {
    loginUrl.searchParams.set('redirect_uri', redirectOverride);
  }

  const response = await fetch(loginUrl.toString(), {
    credentials: 'include',
  });

  const payload: OAuthInitResponse = await response.json().catch(() => ({
    success: false,
    error: 'Unable to parse authentication response.',
  }));

  if (!response.ok || !payload.success || !payload.auth_url) {
    throw new Error(payload.error || 'Failed to start sign-in. Please try again.');
  }

  sessionStorage.setItem('ownership_oauth_provider', provider);
  sessionStorage.setItem('ownership_oauth_return', desiredPostAuthReturn);
  sessionStorage.setItem('ownership_oauth_redirect', ownerPortalReturn);

  window.location.href = payload.auth_url;
};
